"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  TablePagination,
  Tooltip,
} from "@mui/material"
import { Link } from "react-router-dom"
import {
  getTransactions,
  resetTransactionState,
  setFilterModalOpen,
  setFilterType,
  setStartDate,
  setEndDate,
} from "../slices/transactionSlice"
import DashboardLayout from "../components/DashboardLayout"
import { Close as CloseIcon, FilterList as FilterListIcon, Info as InfoIcon } from "@mui/icons-material"

const TransactionManagementPage = () => {
  const dispatch = useDispatch()
  const { userInfo } = useSelector((state) => state.auth || {})
  const {
    creditTransactions = [],
    debitTransactions = [],
    summary = { totalIn: 0, totalOut: 0 },
    loading = false,
    error = null,
    filterModalOpen = false,
    filterType = "all",
    startDate = new Date().toISOString().split("T")[0],
    endDate = new Date().toISOString().split("T")[0],
  } = useSelector((state) => state.transactions || {})

  const [activeTab, setActiveTab] = useState("domestic")
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" })

  // Pagination state
  const [creditPage, setCreditPage] = useState(0)
  const [creditRowsPerPage, setCreditRowsPerPage] = useState(15)
  const [debitPage, setDebitPage] = useState(0)
  const [debitRowsPerPage, setDebitRowsPerPage] = useState(15)

  useEffect(() => {
    // Ensure user is admin

    // Load initial data
    loadTransactions()
  }, [dispatch, activeTab, userInfo?.user?.role])

  useEffect(() => {
    if (error) {
      setAlert({
        open: true,
        message: error,
        severity: "error",
      })
      dispatch(resetTransactionState())
    }
  }, [error, dispatch])

  // Reset pagination when tab changes
  useEffect(() => {
    setCreditPage(0)
    setDebitPage(0)
  }, [activeTab])

  const loadTransactions = () => {
    return dispatch(
      getTransactions({
        creditType: activeTab,
        filterType,
        startDate,
        endDate,
      }),
    )
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false })
  }

  const handleOpenFilterModal = () => {
    dispatch(setFilterModalOpen(true))
  }

  const handleCloseFilterModal = () => {
    dispatch(setFilterModalOpen(false))
  }

  const handleFilterTypeChange = (event) => {
    dispatch(setFilterType(event.target.value))
  }

  const handleStartDateChange = (event) => {
    dispatch(setStartDate(event.target.value))
  }

  const handleEndDateChange = (event) => {
    dispatch(setEndDate(event.target.value))
  }

  const handleApplyFilter = () => {
    loadTransactions()
    handleCloseFilterModal()
    // Reset pagination when filter changes
    setCreditPage(0)
    setDebitPage(0)
  }

  // Credit pagination handlers
  const handleCreditPageChange = (event, newPage) => {
    setCreditPage(newPage)
  }

  const handleCreditRowsPerPageChange = (event) => {
    setCreditRowsPerPage(Number.parseInt(event.target.value, 10))
    setCreditPage(0)
  }

  // Debit pagination handlers
  const handleDebitPageChange = (event, newPage) => {
    setDebitPage(newPage)
  }

  const handleDebitRowsPerPageChange = (event) => {
    setDebitRowsPerPage(Number.parseInt(event.target.value, 10))
    setDebitPage(0)
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
  }

  const formatDateShort = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  // Format user display with name and role - using round brackets
  const formatUserDisplay = (user) => {
    if (!user) return "Unknown User"

    // If user has name and role properties directly
    if (user.name && user.role) {
      return `${user.name} (${user.role})`
    }

    // If user has userName and userRole properties
    if (user.userName && user.userRole) {
      return `${user.userName} (${user.userRole})`
    }

    // If user is just a string (name)
    if (typeof user === "string") {
      // Check if the string already has a role in square brackets and convert to round brackets
      const squareBracketMatch = user.match(/^(.*) \[(.*)\]$/)
      if (squareBracketMatch) {
        return `${squareBracketMatch[1]} (${squareBracketMatch[2]})`
      }
      return user
    }

    // Fallback to ID if available
    return user.id || user._id || "Unknown User"
  }

  const getFilterDisplayText = () => {
    if (filterType === "all") {
      return "All Time"
    } else if (filterType === "singleDay") {
      return formatDateShort(startDate)
    } else if (filterType === "month") {
      const date = new Date(startDate)
      return `${date.toLocaleString("default", { month: "long" })} ${date.getFullYear()}`
    } else if (filterType === "dateRange") {
      return `${formatDateShort(startDate)} to ${formatDateShort(endDate)}`
    }
    return "All Time"
  }

  // Get paginated data
  const paginatedCreditTransactions = creditTransactions.slice(
    creditPage * creditRowsPerPage,
    creditPage * creditRowsPerPage + creditRowsPerPage,
  )

  const paginatedDebitTransactions = debitTransactions.slice(
    debitPage * debitRowsPerPage,
    debitPage * debitRowsPerPage + debitRowsPerPage,
  )

  if (loading && !creditTransactions.length && !debitTransactions.length) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <MuiLink component={Link} to="/dashboard" color="inherit" underline="hover">
            Dashboard
          </MuiLink>
          <Typography color="text.primary">Transaction Management</Typography>
        </Breadcrumbs>

        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.message}
          </Alert>
        </Snackbar>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1">
            {activeTab === "domestic"
              ? "Credit"
              : activeTab === "international"
                ? "International Credit"
                : "Button Credit"}{" "}
            Transactions
          </Typography>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                minWidth: 120,
              },
              "& .Mui-selected": {
                color: "#008069",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#008069",
              },
            }}
          >
            <Tab label="Domestic Credits" value="domestic" />
            <Tab label="International Credits" value="international" />
            <Tab label="Button Credits" value="button" />
          </Tabs>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">{getFilterDisplayText()}</Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleOpenFilterModal}
              sx={{ textTransform: "none", mr: 1 }}
            >
              Date Filter
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Paper sx={{ p: 2, width: "48%" }}>
            <Typography variant="subtitle1" color="text.secondary">
              Total Credits In
            </Typography>
            <Typography variant="h4" color="success.main">
              {summary.totalIn.toLocaleString()}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, width: "48%" }}>
            <Typography variant="subtitle1" color="text.secondary">
              Total Credits Out
            </Typography>
            <Typography variant="h4" color="error.main">
              {summary.totalOut.toLocaleString()}
            </Typography>
          </Paper>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" }, // column on mobile, row on desktop
            gap: 4,
            mb: 4,
            flexWrap: "wrap", // ensures wrapping on smaller screens
          }}>

          <Paper sx={{ p: 3, flex: 1, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Credits In
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : creditTransactions.length > 0 ? (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>SR. NO.</TableCell>
                        <TableCell>DETAILS</TableCell>
                        <TableCell align="right">CREDITS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCreditTransactions.map((transaction, index) => (
                        <TableRow key={transaction._id}>
                          <TableCell>{creditPage * creditRowsPerPage + index + 1}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body1">{formatUserDisplay(transaction.details.name)}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(transaction.details.date)}
                              </Typography>
                              {
                                transaction?.associatedUser !=='' && (
                                  <Typography variant="body1" color="text.primary">
                                  Associated User: {`${transaction?.associatedUser?.name} (${transaction?.associatedUser?.role})` || "Admin"}
                                </Typography>
                                )
                              }
                               
                              <Typography variant="body2" color="text.secondary">
                                By: {formatUserDisplay(transaction.details.by)}
                              </Typography>
                               {transaction.details.description && (() => {
                                  let desc = transaction.details.description;
                                  const targetTrue = " (Report manipulated: true)";
                                  const targetFalse = " (Report manipulated: false)";
                                  if (desc.endsWith(targetTrue)) {
                                    desc = desc.slice(0, -targetTrue.length).trim();
                                  } else if (desc.endsWith(targetFalse)) {
                                    desc = desc.slice(0, -targetFalse.length).trim();
                                  }

                                  return (
                                    <Typography title={"desc"}>
                                      {desc}
                                    </Typography>
                                  );
                                })()}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" color="success.main" fontWeight="bold">
                              {transaction.credits.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={creditTransactions.length}
                  page={creditPage}
                  onPageChange={handleCreditPageChange}
                  rowsPerPage={creditRowsPerPage}
                  onRowsPerPageChange={handleCreditRowsPerPageChange}
                  rowsPerPageOptions={[15, 25, 50, 100]}
                />
              </>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                <Typography variant="body1" color="text.secondary">
                  No credit transactions found
                </Typography>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3, flex: 1, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Credits Out
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : debitTransactions.length > 0 ? (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>SR. NO.</TableCell>
                        <TableCell>DETAILS</TableCell>
                        <TableCell align="right">OUT CREDITS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedDebitTransactions.map((transaction, index) => (
                        <TableRow key={transaction._id}>
                          <TableCell>{debitPage * debitRowsPerPage + index + 1}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body1">{formatUserDisplay(transaction.details.name)}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(transaction.details.date)}
                              </Typography>
                              {
                                 transaction?.associatedUser !=='' &&(
                                  <Typography variant="body1" color="text.primary">
                                  Associated User: {`${transaction?.associatedUser?.name} (${transaction?.associatedUser?.role})` || "Admin"}
                                </Typography>
                                )
                              }
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                
                                <Typography variant="body2" color="text.secondary">
                                  Campaign: {transaction.details.id}
                                </Typography>

                                {transaction.details.description && (() => {
                                  let desc = transaction.details.description;
                                  const targetTrue = " (Report manipulated: true)";
                                  const targetFalse = " (Report manipulated: false)";
                                  if (desc.endsWith(targetTrue)) {
                                    desc = desc.slice(0, -targetTrue.length).trim();
                                  } else if (desc.endsWith(targetFalse)) {
                                    desc = desc.slice(0, -targetFalse.length).trim();
                                  }

                                  return (
                                    <Typography title={desc}>
                                      <InfoIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
                                    </Typography>
                                  );
                                })()}

                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" color="error.main" fontWeight="bold">
                              {transaction.outCredits.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={debitTransactions.length}
                  page={debitPage}
                  onPageChange={handleDebitPageChange}
                  rowsPerPage={debitRowsPerPage}
                  onRowsPerPageChange={handleDebitRowsPerPageChange}
                  rowsPerPageOptions={[15, 25, 50, 100]}
                />
              </>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                <Typography variant="body1" color="text.secondary">
                  No debit transactions found
                </Typography>
              </Box>
            )}
          </Paper>


        </Box>

        {/* Filter Modal */}
        <Dialog open={filterModalOpen} onClose={handleCloseFilterModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              Filter
              <IconButton onClick={handleCloseFilterModal}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <FormControl component="fieldset" sx={{ width: "100%", mt: 1 }}>
              <RadioGroup value={filterType} onChange={handleFilterTypeChange}>
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={<Typography variant="subtitle1">All Time</Typography>}
                />

                <FormControlLabel
                  value="singleDay"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                      <Typography variant="subtitle1">Single Day</Typography>
                      <TextField
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        fullWidth
                        sx={{ mt: 1, mb: 2 }}
                        disabled={filterType !== "singleDay"}
                      />
                    </Box>
                  }
                />

                <FormControlLabel
                  value="month"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                      <Typography variant="subtitle1">Month</Typography>
                      <TextField
                        type="month"
                        value={startDate.substring(0, 7)}
                        onChange={(e) => handleStartDateChange({ target: { value: `${e.target.value}-01` } })}
                        fullWidth
                        sx={{ mt: 1, mb: 2 }}
                        disabled={filterType !== "month"}
                      />
                    </Box>
                  }
                />

                <FormControlLabel
                  value="dateRange"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                      <Typography variant="subtitle1">Date Range</Typography>
                      <Box sx={{ display: "flex", gap: 2, mt: 1, mb: 2 }}>
                        <TextField
                          type="date"
                          label="From"
                          value={startDate}
                          onChange={handleStartDateChange}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          disabled={filterType !== "dateRange"}
                        />
                        <TextField
                          type="date"
                          label="To"
                          value={endDate}
                          onChange={handleEndDateChange}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          disabled={filterType !== "dateRange"}
                        />
                      </Box>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleApplyFilter}
              variant="contained"
              sx={{
                bgcolor: "#008069",
                "&:hover": { bgcolor: "#128C7E" },
                textTransform: "none",
              }}
            >
              Search
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}

export default TransactionManagementPage
